import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { ButtonHTMLAttributes, forwardRef } from "react";

const buttonVariants = cva(
  "font-medium flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300",
  {
    variants: {
      variant: {
        primary:
          "bg-primary hover:bg-primary/90 text-primary-foreground glow-primary",
        secondary:
          "bg-secondary hover:bg-secondary/80 text-secondary-foreground border border-border backdrop-blur-sm",
        accent:
          "gradient-primary hover:opacity-90 text-primary-foreground shadow-xl",
        ghost: "text-muted-foreground hover:text-foreground",
      },
      size: {
        xs: "px-2.5 py-1 text-xs",
        sm: "px-4 py-2 text-sm",
        md: "px-6 py-3 text-sm",
        lg: "px-8 py-3.5",
      },
      rounded: {
        full: "rounded-full",
        "2xl": "rounded-2xl",
        xl: "rounded-xl",
        lg: "rounded-lg",
      },
    },
    defaultVariants: { variant: "primary", size: "lg", rounded: "full" },
  }
);

interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, variant, size, rounded, className, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled}
        className={cn(buttonVariants({ variant, size, rounded }), className)}
        {...props}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
